import { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Search, ShieldOff, Pencil, Upload } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function CRMPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/customers", { params: { q } }); setCustomers(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  const blacklist = async (phone) => { if (!window.confirm("Add to blacklist?")) return; try { await api.post(`/admin/customers/${phone}/blacklist`); toast.success("Blacklisted"); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } };

  // Bulk-import customers from an .xlsx or .csv file. Backend parses the file
  // server-side and upserts by phone — existing customers keep their stats,
  // new customers are inserted with no_show_count=0, total_visits=0.
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    if (!window.confirm(`Import "${file.name}"? Existing customers (by phone) will be updated; new ones will be added.`)) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/customers/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} new + updated ${data.updated} existing customers${data.skipped ? `. Skipped ${data.skipped} bad rows.` : "."}`);
      load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <AdminLayout title="Customers (CRM)" showTurnBoard={false}>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zen-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 h-10 bg-white border-zen-border" data-testid="crm-search" /></div>
        {isOwner && (
          <>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={onImportFile}
              data-testid="crm-import-file"
            />
            <Button variant="outline" disabled={importing}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full" data-testid="crm-import-btn">
              <Upload className="w-4 h-4 mr-1" /> {importing ? "Importing…" : "Import customers"}
            </Button>
          </>
        )}
        {/* Export CSV intentionally removed — owner doesn't want the staff
            to be able to copy the customer list off the device. */}
      </div>
      <div className="bg-white rounded-2xl border border-zen-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-zen-surface text-xs uppercase text-zen-muted"><tr>
            <th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Phone</th><th className="text-left px-5 py-3">Visits</th>
            <th className="text-left px-5 py-3">Last</th><th className="text-left px-5 py-3">No-Shows</th><th className="text-right px-5 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.phone} className="border-t border-zen-border" data-testid={`crm-row-${c.phone}`}>
                <td className="px-5 py-4"><div className="flex items-center gap-2">{c.vip && <Star className="w-4 h-4 fill-zen-gold text-zen-gold" />}<span className="font-medium">{c.name}</span></div></td>
                <td className="px-5 py-4 font-mono text-sm">{c.phone}</td>
                <td className="px-5 py-4">{c.total_visits}</td>
                <td className="px-5 py-4 text-sm text-zen-muted">{c.last_visit || "—"}</td>
                <td className="px-5 py-4">{c.no_show_count}</td>
                <td className="px-5 py-4 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setEditing(c)} data-testid={`crm-edit-${c.phone}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => blacklist(c.phone)} data-testid={`crm-blacklist-${c.phone}`}><ShieldOff className="w-3 h-3 text-red-600" /></Button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td colSpan="6" className="text-center py-12 text-zen-muted">No customers yet</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Customer</DialogTitle></DialogHeader>
          <Edit customer={editing} onSaved={() => { setEditing(null); load(); }} />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Edit({ customer, onSaved }) {
  const [name, setName] = useState(customer?.name || "");
  const [notes, setNotes] = useState(customer?.notes || "");
  const save = async () => { try { await api.patch(`/admin/customers/${customer.phone}`, { name, notes }); toast.success("Saved"); onSaved(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } };
  return (
    <>
      <div className="space-y-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-zen-surface border-0" data-testid="crm-edit-name" />
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-zen-surface border-0 min-h-24" data-testid="crm-edit-notes" />
      </div>
      <DialogFooter><Button onClick={save} className="bg-zen-primary text-white" data-testid="crm-edit-save">Save</Button></DialogFooter>
    </>
  );
}
